import { useEffect, useState, useRef } from "react";

const WS_URL = import.meta.env.VITE_SWING_WS_ENDPOINT; // your backend WS endpoint
let socketInstance: WebSocket | null = null;
let connectionListeners: ((connected: boolean) => void)[] = [];

const setGlobalConnected = (val: boolean) => {
  connectionListeners.forEach(fn => fn(val));
};

// Global handlers store
const handlers: Record<string, Record<string, (data: any) => void>> = {};

export default function useWebSocket() {
  const [connected, setConnected] = useState(socketInstance?.readyState === WebSocket.OPEN);
  
  useEffect(() => {
    connectionListeners.push(setConnected);
    return () => {
      connectionListeners = connectionListeners.filter(fn => fn !== setConnected);
    };
  }, []);

  const connect = () => {
    if (socketInstance) {
      if (socketInstance.readyState === WebSocket.OPEN) {
        setConnected(true);
        return;
      }
      if (socketInstance.readyState === WebSocket.CONNECTING) {
        setConnected(false);
        return;
      }
    }

    console.log("📡 Attempting WebSocket connection to:", WS_URL);
    socketInstance = new WebSocket(WS_URL);

    socketInstance.onopen = () => {
      console.log("✅ WebSocket connected to:", WS_URL);
      setGlobalConnected(true);
    };

    socketInstance.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        const { type, ...data } = message;

        const typeHandlers = handlers[type];
        if (typeHandlers) {
          Object.values(typeHandlers).forEach((fn) => fn(data));
        }
      } catch (err) {
        console.error("Error parsing WebSocket message:", err);
      }
    };

    socketInstance.onclose = () => {
      console.warn("🔴 WebSocket disconnected. Retrying in 5s...");
      setGlobalConnected(false);
      socketInstance = null;
      setTimeout(connect, 5000);
    };

    socketInstance.onerror = (error) => {
      console.error("❌ WebSocket error", error);
      socketInstance?.close();
    };
  };

  useEffect(() => {
    connect();
  }, []);

  const reconnect = () => {
    console.log("🔄 Manual WebSocket reconnect triggered...");
    if (socketInstance) {
      socketInstance.close();
    } else {
      connect();
    }
  };

  /**
   * Register a handler with a unique ID for a message type
   */
  const registerHandler = (
    type: string,
    id: string,
    handler: (data: any) => void
  ) => {
    if (!handlers[type]) {
      handlers[type] = {};
    }
    handlers[type][id] = handler;
  };

  /**
   * Unregister a previously registered handler by ID
   */
  const unregisterHandler = (type: string, id: string) => {
    if (handlers[type]) {
      delete handlers[type][id];
      if (Object.keys(handlers[type]).length === 0) {
        delete handlers[type];
      }
    }
  };

  const sendSubscribe = (tokens: number[]) => {
    if (socketInstance?.readyState === WebSocket.OPEN) {
      socketInstance.send(JSON.stringify({ type: "subscribe", tokens }));
    } else {
      console.warn("WebSocket not open, cannot subscribe.");
    }
  };

  const sendUnsubscribe = (tokens: number[]) => {
    if (socketInstance?.readyState === WebSocket.OPEN) {
      socketInstance.send(JSON.stringify({ type: "unsubscribe", tokens }));
    } else {
      console.warn("WebSocket not open, cannot unsubscribe.");
    }
  };

  const sendMessage = (msg: any) => {
    if (socketInstance?.readyState === WebSocket.OPEN) {
      socketInstance.send(JSON.stringify(msg));
    } else {
      console.warn("WebSocket is not open.");
    }
  };

  return {
    connected,
    reconnect,
    registerHandler,
    unregisterHandler,
    sendSubscribe,
    sendUnsubscribe,
    sendMessage,
  };
}
